// src/components/ReportsScreen.jsx
// Central de Indicadores do PsyAssist Pro.
//
// Decisão de produto:
// - /reports deixa de repetir relatórios financeiros.
// - Financeiro permanece centralizado em /finance.
// - Receita Saúde permanece acessível pela Central Financeira.
// - /reports passa a consolidar indicadores operacionais de:
//   sessões, pacientes, engajamento, agenda e leads.
// - As telas detalhadas continuam existindo como drill-down.
//
// Endpoints utilizados:
// GET /psy/report/sessions?id=ID_USUARIO
// GET /psy/report/patients?id=ID_USUARIO
// GET /psy/report/engagement?id=ID_USUARIO&diasRisco=30
// GET /psy/report/appointment?id=ID_USUARIO
// GET /psy/report/lead?id=ID_USUARIO

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  Clock3,
  LayoutDashboard,
  LoaderCircle,
  Menu,
  RefreshCw,
  Target,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

import "../sass/pages/reports.scss";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const REPORT_DEFINITIONS = [
  {
    id: "sessions",
    title: "Sessões",
    subtitle:
      "Atendimentos realizados, cancelamentos, pagamentos e histórico.",
    path: "/reports/sessions",
    endpoint: "/psy/report/sessions",
    tone: "green",
    icon: CalendarDays,
  },
  {
    id: "patients",
    title: "Pacientes",
    subtitle:
      "Base ativa, novos pacientes, acompanhamento e informações da carteira.",
    path: "/reports/patients",
    endpoint: "/psy/report/patients",
    tone: "blue",
    icon: Users,
  },
  {
    id: "engagement",
    title: "Engajamento",
    subtitle:
      "Aderência, movimento dos atendimentos e pacientes que pedem atenção.",
    path: "/reports/engagement",
    endpoint: "/psy/report/engagement",
    query: {
      diasRisco: 30,
    },
    tone: "violet",
    icon: Activity,
  },
  {
    id: "appointments",
    title: "Agenda",
    subtitle:
      "Consultas, modalidade, cancelamentos e comportamento dos agendamentos.",
    path: "/reports/appointments",
    endpoint: "/psy/report/appointment",
    tone: "cyan",
    icon: Clock3,
  },
  {
    id: "leads",
    title: "Leads",
    subtitle:
      "Entradas comerciais, fechamentos, perdas e origem dos contatos.",
    path: "/reports/leads",
    endpoint: "/psy/report/lead",
    tone: "amber",
    icon: Target,
  },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value =
      sessionStorage.getItem(key) ||
      localStorage.getItem(key);

    if (
      value &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  try {
    sessionStorage.setItem(
      AUTH_KEYS.TOKEN,
      token
    );

    localStorage.setItem(
      AUTH_KEYS.TOKEN,
      token
    );
  } catch {
    // Storage indisponível não deve quebrar o dashboard.
  }
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getTimeGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";

  return "Boa noite";
}

function getTimeBR(value) {
  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--:--";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

async function readApiError(response) {
  try {
    const contentType =
      response.headers.get(
        "content-type"
      ) || "";

    if (
      contentType.includes(
        "application/json"
      )
    ) {
      const payload =
        await response.json();

      return (
        payload?.message ||
        payload?.detail ||
        payload?.error ||
        JSON.stringify(payload)
      );
    }

    const text =
      await response.text();

    return (
      text ||
      `HTTP ${response.status}`
    );
  } catch {
    return `HTTP ${response.status}`;
  }
}

function buildEndpointUrl(
  definition,
  userId
) {
  const params =
    new URLSearchParams();

  params.set("id", userId);

  Object.entries(
    definition?.query || {}
  ).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      String(value).trim() === ""
    ) {
      return;
    }

    params.set(
      key,
      String(value)
    );
  });

  return `${API_BASE}${definition.endpoint}?${params.toString()}`;
}

function getSessionSummary(data) {
  const resumo =
    data?.resumo &&
    typeof data.resumo === "object"
      ? data.resumo
      : {};

  const total = safeNumber(
    resumo?.total ??
      data?.total
  );

  const completed = safeNumber(
    resumo?.concluidas ??
      resumo?.concluida ??
      data?.concluidas
  );

  const canceled = safeNumber(
    resumo?.canceladas ??
      resumo?.cancelada ??
      data?.canceladas
  );

  const cancelRate =
    safeNumber(
      resumo?.taxaCancelamento
    ) ||
    (total > 0
      ? (canceled / total) * 100
      : 0);

  const pending = safeNumber(
    resumo?.pendentes ??
      resumo?.pagamentosPendentes ??
      data?.pendentes
  );

  return {
    primary: total,
    primaryLabel: "sessões",
    secondary: completed,
    secondaryLabel: "concluídas",
    helper: `${percentBR(
      cancelRate
    )}% canceladas`,
    alert:
      cancelRate >= 15
        ? {
            tone:
              cancelRate >= 35
                ? "danger"
                : "warning",
            text: `${percentBR(
              cancelRate
            )}% de cancelamento`,
          }
        : pending > 0
          ? {
              tone: "warning",
              text: `${pending} pagamento(s) pendente(s)`,
            }
          : {
              tone: "good",
              text: "Sessões estáveis",
            },
  };
}

function getPatientSummary(data) {
  const resumo =
    data?.resumo &&
    typeof data.resumo === "object"
      ? data.resumo
      : {};

  const total =
    safeNumber(resumo?.total);

  const active =
    safeNumber(
      resumo?.ativos ??
        data?.ativos
    );

  const newPatients =
    safeNumber(
      resumo?.novos30Dias ??
        resumo?.novos ??
        data?.novos
    );

  const activeRate =
    safeNumber(
      resumo?.taxaAtivos
    ) ||
    (total > 0
      ? (active / total) * 100
      : 0);

  return {
    primary: total,
    primaryLabel: "pacientes",
    secondary: active,
    secondaryLabel: "ativos",
    helper: `${newPatients} novo(s) em 30 dias`,
    alert:
      total <= 0
        ? {
            tone: "neutral",
            text: "Sem pacientes no relatório",
          }
        : activeRate >= 70
          ? {
              tone: "good",
              text: `${percentBR(
                activeRate
              )}% ativos`,
            }
          : {
              tone: "warning",
              text: `${percentBR(
                activeRate
              )}% ativos`,
            },
  };
}

function getEngagementSummary(data) {
  const resumo =
    data?.resumo &&
    typeof data.resumo === "object"
      ? data.resumo
      : {};

  const sessions =
    safeNumber(
      resumo?.totalSessoes ??
        resumo?.sessoes
    );

  const risk =
    safeNumber(
      resumo?.pacientesEmRisco ??
        resumo?.respostas
    );

  const activePercent =
    safeNumber(
      resumo?.percentualPacientesComSessao ??
        resumo?.ativos
    );

  const engagedList =
    Array.isArray(data?.maisEngajados)
      ? data.maisEngajados
      : [];

  const riskList =
    Array.isArray(data?.emRisco)
      ? data.emRisco
      : [];

  return {
    primary:
      sessions ||
      engagedList.length +
        riskList.length,

    primaryLabel:
      sessions
        ? "sessões analisadas"
        : "pacientes analisados",

    secondary:
      risk ||
      riskList.length,

    secondaryLabel: "em atenção",

    helper:
      activePercent > 0
        ? `${percentBR(
            activePercent
          )}% com atividade`
        : `${engagedList.length} com bom engajamento`,

    alert:
      risk > 0 ||
      riskList.length > 0
        ? {
            tone: "warning",
            text: `${
              risk ||
              riskList.length
            } paciente(s) em atenção`,
          }
        : {
            tone: "good",
            text: "Sem alertas de engajamento",
          },
  };
}

function getAppointmentSummary(data) {
  const resumo =
    data?.resumo &&
    typeof data.resumo === "object"
      ? data.resumo
      : {};

  const total =
    safeNumber(
      resumo?.total ??
        data?.total
    );

  const online =
    safeNumber(
      resumo?.online ??
        data?.online
    );

  const presencial =
    safeNumber(
      resumo?.presencial ??
        data?.presencial
    );

  const canceled =
    safeNumber(
      resumo?.cancelado ??
        resumo?.cancelados ??
        data?.cancelado
    );

  const cancelRate =
    safeNumber(
      resumo?.taxaCancelamento
    ) ||
    (total > 0
      ? (canceled / total) * 100
      : 0);

  const predominant =
    online === presencial
      ? "equilibrada"
      : online > presencial
        ? "online"
        : "presencial";

  return {
    primary: total,
    primaryLabel: "agendamentos",
    secondary:
      online + presencial,
    secondaryLabel:
      "online + presencial",
    helper: `modalidade ${predominant}`,
    alert:
      cancelRate >= 15
        ? {
            tone:
              cancelRate >= 35
                ? "danger"
                : "warning",
            text: `${percentBR(
              cancelRate
            )}% cancelados`,
          }
        : {
            tone: "good",
            text: "Agenda estável",
          },
  };
}

function getLeadSummary(data) {
  const resumo =
    data?.resumo &&
    typeof data.resumo === "object"
      ? data.resumo
      : {};

  const total =
    safeNumber(
      data?.total ??
        resumo?.total
    );

  const closed =
    safeNumber(
      data?.fechados ??
        resumo?.fechados
    );

  const lost =
    safeNumber(
      resumo?.perdidos ??
        data?.perdidos
    );

  const conversion =
    safeNumber(
      resumo?.taxaConversao ??
        data?.taxaConversao
    ) ||
    (total > 0
      ? (closed / total) * 100
      : 0);

  return {
    primary: total,
    primaryLabel: "leads",
    secondary: closed,
    secondaryLabel: "fechados",
    helper: `${lost} perdido(s)`,
    alert:
      total <= 0
        ? {
            tone: "neutral",
            text: "Sem leads no período",
          }
        : conversion >= 30
          ? {
              tone: "good",
              text: `${percentBR(
                conversion
              )}% de conversão`,
            }
          : {
              tone: "warning",
              text: `${percentBR(
                conversion
              )}% de conversão`,
            },
  };
}

function summarizeReport(
  id,
  data
) {
  if (id === "sessions") {
    return getSessionSummary(data);
  }

  if (id === "patients") {
    return getPatientSummary(data);
  }

  if (id === "engagement") {
    return getEngagementSummary(data);
  }

  if (id === "appointments") {
    return getAppointmentSummary(data);
  }

  if (id === "leads") {
    return getLeadSummary(data);
  }

  return {
    primary: 0,
    primaryLabel: "registros",
    secondary: 0,
    secondaryLabel: "disponíveis",
    helper: "Abrir relatório",
    alert: {
      tone: "neutral",
      text: "Sem resumo",
    },
  };
}

function OverviewMetric({
  icon: Icon,
  label,
  value,
  description,
  tone,
}) {
  return (
    <article
      className={`rep-metric rep-metric--${tone}`}
    >
      <div className="rep-metric__icon">
        <Icon
          size={19}
          aria-hidden="true"
        />
      </div>

      <div className="rep-metric__content">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function ReportDomainCard({
  definition,
  snapshot,
  error,
  loading,
  onOpen,
}) {
  const Icon = definition.icon;

  return (
    <article
      className={`rep-domain rep-domain--${definition.tone}`}
    >
      <div className="rep-domain__head">
        <div className="rep-domain__identity">
          <div className="rep-domain__icon">
            <Icon
              size={20}
              aria-hidden="true"
            />
          </div>

          <div>
            <span>Relatório detalhado</span>
            <h3>{definition.title}</h3>
          </div>
        </div>

        {loading ? (
          <LoaderCircle
            size={17}
            className="rep-spin"
            aria-label="Carregando"
          />
        ) : error ? (
          <span className="rep-domain__status rep-domain__status--error">
            Indisponível
          </span>
        ) : (
          <span
            className={`rep-domain__status rep-domain__status--${
              snapshot?.alert?.tone ||
              "neutral"
            }`}
          >
            {snapshot?.alert?.text ||
              "Atualizado"}
          </span>
        )}
      </div>

      <p>{definition.subtitle}</p>

      <div className="rep-domain__numbers">
        <div>
          <strong>
            {loading || error
              ? "—"
              : snapshot?.primary ?? 0}
          </strong>

          <span>
            {snapshot?.primaryLabel ||
              "registros"}
          </span>
        </div>

        <i aria-hidden="true" />

        <div>
          <strong>
            {loading || error
              ? "—"
              : snapshot?.secondary ?? 0}
          </strong>

          <span>
            {snapshot?.secondaryLabel ||
              "indicadores"}
          </span>
        </div>
      </div>

      <div className="rep-domain__footer">
        <small>
          {error
            ? "O resumo não carregou. O relatório detalhado continua disponível."
            : snapshot?.helper ||
              "Abra para analisar filtros e detalhes."}
        </small>

        <button
          type="button"
          onClick={onOpen}
        >
          Abrir relatório
          <ArrowRight size={15} />
        </button>
      </div>
    </article>
  );
}

function PriorityItem({
  icon: Icon,
  label,
  value,
  description,
  tone,
  onClick,
}) {
  return (
    <button
      className={`rep-priority rep-priority--${tone}`}
      type="button"
      onClick={onClick}
    >
      <div className="rep-priority__icon">
        <Icon
          size={18}
          aria-hidden="true"
        />
      </div>

      <div className="rep-priority__content">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>

      <ArrowRight
        size={16}
        className="rep-priority__arrow"
        aria-hidden="true"
      />
    </button>
  );
}

function CentralSkeleton() {
  return (
    <div
      className="rep-skeleton"
      aria-hidden="true"
    >
      <div className="rep-skeleton__hero" />

      <div className="rep-skeleton__metrics">
        <span />
        <span />
        <span />
        <span />
      </div>

      <div className="rep-skeleton__panel" />

      <div className="rep-skeleton__cards">
        <span />
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export default function ReportsScreen() {
  const navigate = useNavigate();
  const toast = useToast();

  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [reports, setReports] =
    useState({});

  const [errors, setErrors] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [lastUpdatedAt, setLastUpdatedAt] =
    useState(null);

  const nomeUsuario = useMemo(
    () =>
      readStorage([
        AUTH_KEYS.USER_NAME,
      ]) || "",
    []
  );

  const firstName = useMemo(
    () => getFirstName(nomeUsuario),
    [nomeUsuario]
  );

  const authOrRedirect =
    useCallback(() => {
      const token =
        readStorage([
          AUTH_KEYS.TOKEN,
          "token",
        ]);

      const userId =
        readStorage([
          AUTH_KEYS.USER_ID,
          "id",
        ]);

      if (!token || !userId) {
        toast.error({
          title: "Sessão encerrada",
          message:
            "Faça login novamente para acessar a Central de Indicadores.",
          duration: 6500,
        });

        navigate("/login", {
          replace: true,
        });

        return null;
      }

      return {
        token,
        userId,
      };
    }, [navigate, toast]);

  const fetchDefinition =
    useCallback(
      async (
        definition,
        auth,
        signal
      ) => {
        const response =
          await fetch(
            buildEndpointUrl(
              definition,
              auth.userId
            ),
            {
              method: "GET",
              headers: {
                Authorization:
                  `Bearer ${auth.token}`,
                "Content-Type":
                  "application/json",
              },
              signal,
            }
          );

        saveAuthToken(
          response.headers.get(
            "x-new-token"
          )
        );

        if (
          response.status === 401 ||
          response.status === 403
        ) {
          const error =
            new Error(
              "Sessão expirada."
            );

          error.code =
            "AUTH_EXPIRED";

          throw error;
        }

        if (!response.ok) {
          const message =
            await readApiError(
              response
            );

          throw new Error(
            message ||
              `Falha ao carregar ${definition.title}.`
          );
        }

        return await response
          .json()
          .catch(() => null);
      },
      []
    );

  const loadCentral =
    useCallback(
      async ({
        signal,
        silent = false,
      } = {}) => {
        const auth =
          authOrRedirect();

        if (!auth) {
          setLoading(false);
          return;
        }

        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const nextReports = {};
        const nextErrors = {};

        try {
          const results =
            await Promise.allSettled(
              REPORT_DEFINITIONS.map(
                async (definition) => {
                  const data =
                    await fetchDefinition(
                      definition,
                      auth,
                      signal
                    );

                  return {
                    id: definition.id,
                    data,
                  };
                }
              )
            );

          if (
            signal?.aborted
          ) {
            return;
          }

          let authExpired = false;

          results.forEach(
            (result, index) => {
              const definition =
                REPORT_DEFINITIONS[
                  index
                ];

              if (
                result.status ===
                "fulfilled"
              ) {
                nextReports[
                  definition.id
                ] = result.value.data;

                return;
              }

              const reason =
                result.reason;

              if (
                reason?.name ===
                "AbortError"
              ) {
                return;
              }

              if (
                reason?.code ===
                "AUTH_EXPIRED"
              ) {
                authExpired = true;
                return;
              }

              nextErrors[
                definition.id
              ] =
                reason?.message ||
                "Não foi possível carregar este resumo.";
            }
          );

          if (authExpired) {
            toast.error({
              title:
                "Sessão encerrada",
              message:
                "Sua sessão expirou. Faça login novamente.",
              duration: 6500,
            });

            navigate("/login", {
              replace: true,
            });

            return;
          }

          setReports(
            nextReports
          );

          setErrors(
            nextErrors
          );

          setLastUpdatedAt(
            new Date()
          );

          const errorCount =
            Object.keys(
              nextErrors
            ).length;

          if (
            silent &&
            errorCount === 0
          ) {
            toast.success({
              title:
                "Indicadores atualizados",
              message:
                "Os resumos operacionais foram atualizados.",
            });
          }

          if (
            errorCount > 0 &&
            errorCount <
              REPORT_DEFINITIONS.length
          ) {
            toast.warning?.({
              title:
                "Atualização parcial",
              message:
                `${errorCount} área(s) não responderam. As demais informações continuam disponíveis.`,
            });
          }
        } catch (error) {
          if (
            error?.name ===
            "AbortError"
          ) {
            return;
          }

          console.error(
            "Erro ao carregar Central de Indicadores:",
            error
          );

          toast.error({
            title:
              "Indicadores indisponíveis",
            message:
              "Não foi possível carregar os resumos agora.",
          });
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
      },
      [
        authOrRedirect,
        fetchDefinition,
        navigate,
        toast,
      ]
    );

  useEffect(() => {
    const controller =
      new AbortController();

    loadCentral({
      signal:
        controller.signal,
    });

    return () =>
      controller.abort();
  }, [loadCentral]);

  const snapshots = useMemo(() => {
    return REPORT_DEFINITIONS.reduce(
      (acc, definition) => {
        acc[definition.id] =
          summarizeReport(
            definition.id,
            reports[
              definition.id
            ]
          );

        return acc;
      },
      {}
    );
  }, [reports]);

  const sessions =
    snapshots.sessions || {};

  const patients =
    snapshots.patients || {};

  const leads =
    snapshots.leads || {};

  const leadConversion =
    useMemo(() => {
      const data =
        reports.leads;

      const total =
        safeNumber(
          data?.total ??
            data?.resumo?.total
        );

      const closed =
        safeNumber(
          data?.fechados ??
            data?.resumo
              ?.fechados
        );

      return total > 0
        ? (closed / total) *
            100
        : 0;
    }, [reports.leads]);

  const cancelRate =
    useMemo(() => {
      const data =
        reports.sessions;

      const resumo =
        data?.resumo || {};

      const total =
        safeNumber(
          resumo?.total ??
            data?.total
        );

      const canceled =
        safeNumber(
          resumo?.canceladas ??
            resumo?.cancelada ??
            data?.canceladas
        );

      return (
        safeNumber(
          resumo?.taxaCancelamento
        ) ||
        (total > 0
          ? (canceled /
              total) *
            100
          : 0)
      );
    }, [reports.sessions]);

  const engagementRisk =
    safeNumber(
      reports.engagement
        ?.resumo
        ?.pacientesEmRisco ??
        reports.engagement
          ?.resumo
          ?.respostas
    ) ||
    (Array.isArray(
      reports.engagement
        ?.emRisco
    )
      ? reports.engagement
          .emRisco.length
      : 0);

  const openReport = (path) => {
    if (!path) return;

    navigate(path);
  };

  if (
    loading &&
    !Object.keys(reports).length
  ) {
    return (
      <div className="rep-page">
        <div
          className="rep-bg"
          aria-hidden="true"
        >
          <span className="rep-bg__orb rep-bg__orb--one" />
          <span className="rep-bg__orb rep-bg__orb--two" />
        </div>

        <AppSidebar
          open={sidebarOpen}
          onClose={() =>
            setSidebarOpen(false)
          }
        />

        <CentralSkeleton />
      </div>
    );
  }

  return (
    <div className="rep-page">
      <div
        className="rep-bg"
        aria-hidden="true"
      >
        <span className="rep-bg__orb rep-bg__orb--one" />
        <span className="rep-bg__orb rep-bg__orb--two" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />

      <header className="rep-topbar">
        <button
          className="rep-icon-btn"
          type="button"
          onClick={() =>
            setSidebarOpen(true)
          }
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="rep-brand">
          <img
            src={Logo}
            alt="PsyAssist"
            className="rep-brand__logo"
          />

          <div className="rep-brand__text">
            <strong>
              Indicadores
            </strong>

            <span>
              Operação, pacientes e crescimento
              {" • "}
              {nomeUsuario ||
                "Usuário"}
            </span>
          </div>
        </div>

        <div className="rep-top-actions">
          <button
            className="rep-btn rep-btn--ghost rep-btn--desktop"
            type="button"
            onClick={() =>
              navigate(
                "/dashboard"
              )
            }
          >
            <LayoutDashboard
              size={16}
            />
            Início
          </button>

          <button
            className="rep-btn rep-btn--primary"
            type="button"
            onClick={() =>
              loadCentral({
                silent: true,
              })
            }
            disabled={
              loading ||
              refreshing
            }
          >
            <RefreshCw
              size={16}
              className={
                refreshing
                  ? "rep-spin"
                  : ""
              }
            />

            {refreshing
              ? "Atualizando..."
              : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="rep-main">
        <section className="rep-hero">
          <div className="rep-hero__content">
            <span className="rep-kicker">
              <BarChart3
                size={14}
              />
              Central de indicadores
            </span>

            <h1>
              Entenda a operação
              sem abrir cinco telas.
            </h1>

            <p>
              {getTimeGreeting()}
              {firstName
                ? `, ${firstName}`
                : ""}
              . Acompanhe os principais
              sinais de sessões,
              pacientes, agenda,
              engajamento e captação em
              uma visão única. Os
              relatórios detalhados ficam
              disponíveis quando você
              precisar aprofundar.
            </p>

            <div className="rep-hero__actions">
              <button
                className="rep-btn rep-btn--hero-primary"
                type="button"
                onClick={() =>
                  openReport(
                    "/reports/sessions"
                  )
                }
              >
                <CalendarDays
                  size={16}
                />
                Analisar sessões
              </button>

              <button
                className="rep-btn rep-btn--hero-ghost"
                type="button"
                onClick={() =>
                  openReport(
                    "/reports/patients"
                  )
                }
              >
                <Users size={16} />
                Ver pacientes
              </button>
            </div>
          </div>

          <aside className="rep-hero-card">
            <div className="rep-hero-card__icon">
              <Activity
                size={22}
              />
            </div>

            <div>
              <small>
                Leitura operacional
              </small>

              <strong>
                {
                  REPORT_DEFINITIONS.length
                } áreas conectadas
              </strong>

              <span>
                Financeiro foi movido
                integralmente para sua
                Central Financeira.
              </span>
            </div>

            {lastUpdatedAt ? (
              <div className="rep-hero-card__updated">
                <RefreshCw
                  size={12}
                />
                Atualizado às{" "}
                {getTimeBR(
                  lastUpdatedAt
                )}
              </div>
            ) : null}
          </aside>
        </section>

        <section
          className="rep-metrics"
          aria-label="Resumo dos indicadores"
        >
          <OverviewMetric
            icon={CalendarDays}
            label="Sessões"
            value={
              errors.sessions
                ? "—"
                : sessions.primary ??
                  0
            }
            description={
              errors.sessions
                ? "Resumo indisponível"
                : sessions.helper ||
                  "Atendimentos"
            }
            tone="green"
          />

          <OverviewMetric
            icon={UserCheck}
            label="Pacientes ativos"
            value={
              errors.patients
                ? "—"
                : patients.secondary ??
                  0
            }
            description={
              errors.patients
                ? "Resumo indisponível"
                : patients.helper ||
                  "Base ativa"
            }
            tone="blue"
          />

          <OverviewMetric
            icon={AlertCircle}
            label="Em atenção"
            value={
              errors.engagement
                ? "—"
                : engagementRisk
            }
            description="Sinais de engajamento"
            tone={
              engagementRisk > 0
                ? "amber"
                : "violet"
            }
          />

          <OverviewMetric
            icon={TrendingUp}
            label="Conversão de leads"
            value={
              errors.leads
                ? "—"
                : `${percentBR(
                    leadConversion
                  )}%`
            }
            description={
              errors.leads
                ? "Resumo indisponível"
                : `${leads.secondary || 0} fechado(s)`
            }
            tone="violet"
          />
        </section>

        <section className="rep-panel rep-panel--priorities">
          <div className="rep-panel__head">
            <div>
              <span>
                Leitura rápida
              </span>

              <h2>
                O que merece atenção
              </h2>

              <p>
                Sinais operacionais
                derivados dos relatórios
                atuais. Clique para abrir
                o contexto completo.
              </p>
            </div>
          </div>

          <div className="rep-priorities">
            <PriorityItem
              icon={
                cancelRate >= 15
                  ? AlertCircle
                  : CheckCircle2
              }
              label="Cancelamentos"
              value={
                errors.sessions
                  ? "Indisponível"
                  : `${percentBR(
                      cancelRate
                    )}%`
              }
              description={
                errors.sessions
                  ? "Não foi possível ler sessões."
                  : cancelRate >= 35
                    ? "Taxa alta no período."
                    : cancelRate >= 15
                      ? "Vale acompanhar a agenda."
                      : "Dentro de uma faixa estável."
              }
              tone={
                errors.sessions
                  ? "neutral"
                  : cancelRate >= 35
                    ? "danger"
                    : cancelRate >= 15
                      ? "warning"
                      : "good"
              }
              onClick={() =>
                openReport(
                  "/reports/sessions"
                )
              }
            />

            <PriorityItem
              icon={
                engagementRisk > 0
                  ? AlertCircle
                  : CheckCircle2
              }
              label="Engajamento"
              value={
                errors.engagement
                  ? "Indisponível"
                  : `${engagementRisk} paciente(s)`
              }
              description={
                errors.engagement
                  ? "Não foi possível ler engajamento."
                  : engagementRisk > 0
                    ? "Há pacientes que pedem acompanhamento."
                    : "Nenhum alerta operacional agora."
              }
              tone={
                errors.engagement
                  ? "neutral"
                  : engagementRisk > 0
                    ? "warning"
                    : "good"
              }
              onClick={() =>
                openReport(
                  "/reports/engagement"
                )
              }
            />

            <PriorityItem
              icon={TrendingUp}
              label="Captação"
              value={
                errors.leads
                  ? "Indisponível"
                  : `${percentBR(
                      leadConversion
                    )}% conversão`
              }
              description={
                errors.leads
                  ? "Não foi possível ler leads."
                  : leadConversion >= 30
                    ? "Boa relação entre leads e fechamentos."
                    : leads.primary > 0
                      ? "Há espaço para melhorar conversão."
                      : "Ainda sem leads para comparar."
              }
              tone={
                errors.leads
                  ? "neutral"
                  : leadConversion >= 30
                    ? "good"
                    : leads.primary > 0
                      ? "warning"
                      : "neutral"
              }
              onClick={() =>
                openReport(
                  "/reports/leads"
                )
              }
            />
          </div>
        </section>

        <section className="rep-panel">
          <div className="rep-panel__head rep-panel__head--with-action">
            <div>
              <span>
                Relatórios detalhados
              </span>

              <h2>
                Aprofunde somente
                quando precisar
              </h2>

              <p>
                A visão central resolve
                a leitura diária. Estes
                painéis ficam para
                filtros, histórico e
                análises específicas.
              </p>
            </div>

            <div className="rep-panel__count">
              {
                REPORT_DEFINITIONS.length
              } relatórios
            </div>
          </div>

          <div className="rep-domains">
            {REPORT_DEFINITIONS.map(
              (definition) => (
                <ReportDomainCard
                  key={definition.id}
                  definition={
                    definition
                  }
                  snapshot={
                    snapshots[
                      definition.id
                    ]
                  }
                  error={
                    errors[
                      definition.id
                    ]
                  }
                  loading={
                    refreshing
                  }
                  onOpen={() =>
                    openReport(
                      definition.path
                    )
                  }
                />
              )
            )}
          </div>
        </section>

        <section className="rep-separation">
          <div className="rep-separation__icon">
            <CircleDot
              size={18}
            />
          </div>

          <div>
            <strong>
              Financeiro agora é uma
              área própria
            </strong>

            <span>
              Receitas, despesas, saldo,
              análise financeira,
              cobranças e Receita Saúde
              não são mais duplicados
              aqui.
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              navigate("/finance")
            }
          >
            Abrir financeiro
            <ArrowRight
              size={15}
            />
          </button>
        </section>
      </main>
    </div>
  );
}
